import React, { useState, useMemo, useEffect } from 'react';
import { Space, Tooltip, Switch, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faImage, faMapMarkerAlt, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../context/ThemeContext';
import Table from '../../commonComponents/Table';
import TablePagination from '../../commonComponents/Pagination';
import {
  ActionIconButton,
  AddressText,
  TableCard,
  TableWrapper,
  StoreNameCell,
  StoreThumbnailWrap,
  StoreThumbnailImg,
  StoreNoImagePlaceholder,
  StoreEyeBadge,
  StorePreviewImageWrapper,
  StorePreviewImg,
} from './styles';
import { LABELS, MESSAGES, toSafeImageUrl } from './constants';
import type { StoreTableProps } from './types';
import type { ColumnsType } from 'antd/es/table';
import type { Store } from './store/userStoresSlice';

const StoreTable: React.FC<StoreTableProps> = ({
  stores,
  loading,
  isAdmin = false,
  onEdit,
  onDelete,
  onToggleAutoAccept,
  canToggleAutoAccept = false,
  togglingStoreId = null,
}) => {
  const { isDarkMode } = useTheme();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [previewStore, setPreviewStore] = useState<Store | null>(null);

  const paginatedStores = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return stores.slice(start, end);
  }, [stores, currentPage, pageSize]);

  // Admin-only: `stores` is a new filtered result whenever the admin search/
  // merchant filter changes — jump back to page 1 so filtering never strands
  // the user on a now out-of-range page. Merchant/manager/staff have no
  // filter UI, so their pagination behavior on create/edit/delete stays
  // exactly as it was before this feature.
  useEffect(() => {
    if (isAdmin) {
      setCurrentPage(1);
    }
  }, [stores, isAdmin]);

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const columns: ColumnsType<Store> = [
    {
      title: 'Store Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Store) => {
        const imageUrl = toSafeImageUrl(record.imageUrl);
        return (
          <StoreNameCell>
            <StoreThumbnailWrap>
              {imageUrl ? (
                <>
                  <StoreThumbnailImg
                    src={imageUrl}
                    alt={text}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <StoreEyeBadge onClick={(e) => { e.stopPropagation(); setPreviewStore(record); }}>
                    <FontAwesomeIcon icon={faEye} fontSize={9} color="#fff" />
                  </StoreEyeBadge>
                </>
              ) : (
                <StoreNoImagePlaceholder>
                  <FontAwesomeIcon icon={faImage} fontSize={18} />
                </StoreNoImagePlaceholder>
              )}
            </StoreThumbnailWrap>
            <strong>{text}</strong>
          </StoreNameCell>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            title: 'Merchant Name',
            key: 'merchant',
            render: (_: unknown, record: Store) => record.merchant?.name || '—',
          } as ColumnsType<Store>[number],
        ]
      : []),
    {
      title: 'Address',
      key: 'address',
      render: (_, record: Store) => (
        <div>
          <div>{record.address || 'No address provided'}</div>
          {record.city && record.state && (
            <AddressText>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {record.city}, {record.state} {record.zipCode}
            </AddressText>
          )}
        </div>
      ),
    },
    {
      title: 'Prep Time',
      key: 'estimatedPreparationTime',
      render: (_, record: Store) =>
        record.estimatedPreparationTime ? `${record.estimatedPreparationTime} min` : '—',
    },
    {
      title: 'Tax',
      key: 'tax',
      align: 'center',
      width: 100,
      render: (_, record: Store) =>
        record.taxable && Number(record.taxRate) > 0
          ? `${(Number(record.taxRate) * 100).toFixed(3).replace(/\.?0+$/, '')}%`
          : '—',
    },
    {
      title: LABELS.AUTO_ACCEPT_ORDERS,
      key: 'autoAcceptOrders',
      align: 'center',
      width: 140,
      render: (_, record: Store) => (
        <Tooltip title={canToggleAutoAccept ? undefined : MESSAGES.AUTO_ACCEPT_ACTION_DISABLED}>
          <Switch
            checked={!!record.autoAcceptOrders}
            disabled={!canToggleAutoAccept || togglingStoreId !== null}
            loading={togglingStoreId === record.id}
            onChange={(checked) => onToggleAutoAccept(record.id, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record: Store) => (
        <Space size="small">
          <Tooltip title={isAdmin ? undefined : MESSAGES.MERCHANT_ACTION_DISABLED}>
            <ActionIconButton
              onClick={isAdmin ? () => onEdit(record) : undefined}
              iconColor="#2D6CCA"
              style={isAdmin ? undefined : { cursor: 'not-allowed' }}
            >
              <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
            </ActionIconButton>
          </Tooltip>
          <Tooltip title={isAdmin ? undefined : MESSAGES.MERCHANT_ACTION_DISABLED}>
            <ActionIconButton
              onClick={isAdmin ? () => onDelete(record.id) : undefined}
              iconColor="#f5222d"
              style={isAdmin ? undefined : { cursor: 'not-allowed' }}
            >
              <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? "#fff" : "#222222"} />
            </ActionIconButton>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <TableWrapper>
      <TableCard>
        <Table<Store>
          columns={columns}
          dataSource={paginatedStores}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={stores.length > 0 ? { x: true, y: 600 } : undefined}
          emptyText={MESSAGES.NO_STORES}
          {...(isAdmin
            ? {
                emptyDescription: (
                  <div>
                    <div>{MESSAGES.NO_STORES}</div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                      {MESSAGES.GET_STARTED_ADMIN}
                    </div>
                  </div>
                ),
              }
            : {})}
          headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
          removeVerticalBorders={true}
          thPadding="15px 10px"
          tdPadding="10px 10px"
          firstChildBorderRadius="8px"
          border="0"
          borderBottomLeftRight={true}
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </TableCard>
      {stores.length > 0 && (
        <TablePagination
          totalCount={stores.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total stores"
          padding="16px 12px 0px 12px"
        />
      )}

      <Modal
        open={!!previewStore}
        onCancel={() => setPreviewStore(null)}
        centered
        width="auto"
        title={previewStore?.name}
        footer={null}
        styles={{ body: { padding: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' } }}
      >
        <StorePreviewImageWrapper>
          <StorePreviewImg src={toSafeImageUrl(previewStore?.imageUrl)} alt={previewStore?.name} />
        </StorePreviewImageWrapper>
      </Modal>
    </TableWrapper>
  );
};

export default React.memo(StoreTable);
